import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import useSiteContent from "@/lib/useSiteContent";
import usePlatformConfig from "@/lib/usePlatformConfig";
import { ArrowRight } from "lucide-react";
import SiteLink from "./SiteLink";

/**
 * Final call to action + the site footer.
 *
 * The footer's link columns are Super Admin → Site → Links rows (contract §3.3): one
 * column per footer group, in the registry's FOOTER_GROUPS order, under that group's
 * editable heading. A group with no link is not rendered at all — an empty heading
 * advertises a section that has nothing in it.
 */
export default function FinalCta() {
  const { text, image, links } = useSiteContent();
  const { trialPeriodDays: days } = usePlatformConfig();
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const logo = image("image.brand.logoFooter", "/brand/solunary-logo.png");

  // `days == null` until `GET /saas/config` resolves (or if the server ever omits the
  // field): the digit-free generic sentence covers first paint instead of interpolating
  // "undefined-day trial". An operator's override of either sentence is interpolated with
  // the same `{days}` as the default.
  const finalCtaSubtitle = days != null ? text("finalCta.subtitle", { days }) : text("pricing.subtitle");

  const footerColumns = [
    { group: "product", heading: text("footer.product"), items: links("footer", "product") },
    { group: "company", heading: text("footer.company"), items: links("footer", "company") },
    { group: "legal", heading: text("footer.legal"), items: links("footer", "legal") },
  ].filter((column) => column.items.length > 0);

  return (
    <>
      <section className="py-28 bg-sol-midnight text-sol-ivory">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-5xl lg:text-6xl leading-tight">
            {text("finalCta.title")}
          </h2>
          <p className="mt-6 text-sol-ivory/60 max-w-xl mx-auto text-lg">
            {finalCtaSubtitle}
          </p>
          <Button
            data-testid="cta-footer-signup"
            onClick={() => navigate("/platform/signup")}
            className="mt-12 bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight rounded-2xl px-10 py-7 text-base font-semibold"
          >
            {text("finalCta.cta")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="bg-sol-midnight border-t border-sol-ivory/10 pt-16 pb-10" data-testid="platform-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img src={logo.src} alt={logo.alt || "Solunary"} className="h-16 w-16 object-contain -my-2" />
                <span className="font-sol font-semibold text-xl text-sol-ivory">Solunary</span>
              </div>
              <p className="text-sm text-sol-ivory/50 leading-relaxed">{text("footer.tagline")}</p>
            </div>

            {footerColumns.map((column) => (
              <div key={column.group} data-testid={`footer-group-${column.group}`}>
                <div className="text-xs uppercase tracking-wider text-sol-ivory/40 mb-4">{column.heading}</div>
                <ul className="space-y-2.5 text-sm">
                  {column.items.map((link) => (
                    <li key={link.id}>
                      <SiteLink
                        link={link}
                        testId={`footer-link-${link.id}`}
                        className="text-sol-ivory/70 hover:text-sol-gold transition"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="pt-8 border-t border-sol-ivory/10 text-center text-sm text-sol-ivory/40">
            {text("footer.copyright", { year })}
          </div>
        </div>
      </footer>
    </>
  );
}
